import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import vulnerabilityAPI, {
  Vulnerability,
  DashboardMetrics,
  QuarterlyReport,
  VulnerabilitySettings
} from '../services/vulnerabilityAPI';
import {
  Shield,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Search,
  Filter,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  FileText,
  Bell,
  Settings,
  Calendar,
  Save,
  Loader2
} from 'lucide-react';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';

type TabType = 'overview' | 'vulnerabilities' | 'reports' | 'settings';
type VulnerabilityStatus = 'open' | 'in_progress' | 'resolved' | 'risk_accepted' | 'false_positive';

interface VulnerabilityDashboardProps {
  onViewVulnerability?: (id: number) => void;
}

const VulnerabilityDashboard: React.FC<VulnerabilityDashboardProps> = ({ onViewVulnerability }) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [vulnerabilities, setVulnerabilities] = useState<Vulnerability[]>([]);
  const [quarterlyReport, setQuarterlyReport] = useState<QuarterlyReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    severity: '',
    source: ''
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [settings, setSettings] = useState<VulnerabilitySettings | null>(null);
  const [editedSettings, setEditedSettings] = useState<VulnerabilitySettings | null>(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [hasSettingsChanges, setHasSettingsChanges] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'settings' && !settings) {
      loadSettings();
    }
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [metricsData, vulnData, reportData] = await Promise.all([
        vulnerabilityAPI.getDashboardMetrics(),
        vulnerabilityAPI.getVulnerabilities({ limit: 50 }),
        vulnerabilityAPI.getQuarterlyReport()
      ]);
      setMetrics(metricsData);
      setVulnerabilities(vulnData);
      setQuarterlyReport(reportData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const settingsData = await vulnerabilityAPI.getSettings();
      setSettings(settingsData);
      setEditedSettings(settingsData);
      setHasSettingsChanges(false);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    }
  };

  const handleSettingsChange = (
    field: keyof VulnerabilitySettings,
    value: number | boolean | Array<'SUPER_ADMIN' | 'ADMIN'>
  ) => {
    if (!editedSettings) return;
    setEditedSettings({ ...editedSettings, [field]: value });
    setHasSettingsChanges(true);
  };

  const recipientRolesValue = (editedSettings?.notify_recipient_roles || ['SUPER_ADMIN']).includes('ADMIN')
    ? 'super_and_admin'
    : 'super_only';

  const handleSaveSettings = async () => {
    if (!editedSettings) return;
    setIsSavingSettings(true);
    try {
      const updatedSettings = await vulnerabilityAPI.updateSettings(editedSettings);
      setSettings(updatedSettings);
      setEditedSettings(updatedSettings);
      setHasSettingsChanges(false);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleResetSettings = () => {
    if (settings) {
      setEditedSettings(settings);
      setHasSettingsChanges(false);
    }
  };

  const handleScanNpmAudit = async () => {
    setIsScanning(true);
    try {
      const result = await vulnerabilityAPI.scanNpmAudit();
      if (result.imported > 0) {
        toast.success(`Scan completed: ${result.imported} new vulnerabilities imported`, { duration: 5000 });
      } else if (result.found > 0) {
        toast.success(`Scan completed: ${result.found} vulnerabilities found (${result.skipped} already tracked)`, { duration: 5000 });
      } else {
        toast.success('Scan completed: No vulnerabilities found!', { duration: 4000 });
      }
      await loadData();
    } catch (error) {
      console.error('Failed to run npm audit:', error);
      toast.error('Failed to run npm audit scan');
    } finally {
      setIsScanning(false);
    }
  };

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      const blob = await vulnerabilityAPI.downloadQuarterlyReportPDF();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vulnerability-report-Q${quarterlyReport?.period.quarter}-${quarterlyReport?.period.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download report');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadData();
      toast.success('Data refreshed');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleVulnerabilityClick = (id: number) => {
    if (onViewVulnerability) {
      onViewVulnerability(id);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string, skipReload = false) => {
    try {
      await vulnerabilityAPI.updateVulnerability(id, { status: newStatus });
      if (!skipReload) {
        await refreshData();
      }
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
      await refreshData();
    }
  };

  const refreshData = async () => {
    try {
      const [metricsData, vulnData, reportData] = await Promise.all([
        vulnerabilityAPI.getDashboardMetrics(),
        vulnerabilityAPI.getVulnerabilities({ limit: 50 }),
        vulnerabilityAPI.getQuarterlyReport()
      ]);
      setMetrics(metricsData);
      setVulnerabilities(vulnData);
      setQuarterlyReport(reportData);
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      zero_day: 'bg-purple-100 text-purple-800 border-purple-200',
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-amber-100 text-amber-800 border-amber-200',
      low: 'bg-green-100 text-green-800 border-green-200',
      info: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getSLAStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      on_track: 'text-green-600',
      warning: 'text-amber-600',
      critical: 'text-orange-600',
      breached: 'text-red-600',
      completed: 'text-blue-600'
    };
    return colors[status] || 'text-gray-600';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      open: 'bg-red-100 text-red-800',
      in_progress: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      risk_accepted: 'bg-amber-100 text-amber-800',
      false_positive: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredVulnerabilities = vulnerabilities.filter(v => {
    if (searchTerm && !v.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !v.cve_id?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !v.package_name?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filters.status && v.status !== filters.status) return false;
    if (filters.severity && v.severity !== filters.severity) return false;
    if (filters.source && v.source !== filters.source) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading vulnerability data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vulnerability Management</h1>
          <p className="text-sm text-gray-500 mt-1">Track, remediate, and manage security vulnerabilities</p>
        </div>
        <div className="flex items-center gap-3">
          <AdminSecurityHelpButton screenId="vulnerability-management" />
          <button
            onClick={handleScanNpmAudit}
            disabled={isScanning}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={isScanning ? 'animate-spin' : ''} />
            {isScanning ? 'Scanning...' : 'Run npm Audit'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {(['overview', 'vulnerabilities', 'reports', 'settings'] as TabType[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && metrics && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Total</span>
                <Shield size={18} className="text-blue-500" />
              </div>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{metrics.total}</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Open</span>
                <AlertTriangle size={18} className="text-red-500" />
              </div>
              <p className="text-2xl font-semibold text-red-600 mt-1">{metrics.open}</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">In Progress</span>
                <Clock size={18} className="text-blue-500" />
              </div>
              <p className="text-2xl font-semibold text-blue-600 mt-1">{metrics.inProgress}</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Resolved</span>
                <CheckCircle size={18} className="text-green-500" />
              </div>
              <p className="text-2xl font-semibold text-green-600 mt-1">{metrics.resolved}</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">SLA Breached</span>
                <XCircle size={18} className="text-red-500" />
              </div>
              <p className={`text-2xl font-semibold mt-1 ${metrics.breached > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {metrics.breached}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Due Soon</span>
                <Clock size={18} className="text-amber-500" />
              </div>
              <p className={`text-2xl font-semibold mt-1 ${metrics.dueSoon > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                {metrics.dueSoon}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">By Severity</h3>
              <div className="space-y-3">
                {Object.entries(metrics.bySeverity).map(([severity, count]) => (
                  <div key={severity} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${getSeverityColor(severity)}`}>
                        {severity.toUpperCase()}
                      </span>
                    </div>
                    <span className="font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Metrics</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">SLA Compliance</span>
                    <span className="text-sm font-semibold">{metrics.slaCompliance}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${metrics.slaCompliance >= 90 ? 'bg-green-500' : metrics.slaCompliance >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${metrics.slaCompliance}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Resolution Score</span>
                    <span className="text-sm font-semibold">{metrics.complianceScore}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${metrics.complianceScore >= 80 ? 'bg-green-500' : metrics.complianceScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${metrics.complianceScore}%` }}
                    ></div>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Mean Time to Remediate</span>
                    <span className="text-lg font-semibold text-gray-900">{metrics.mttrDays} days</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">By Source</h3>
              <div className="space-y-3">
                {Object.entries(metrics.bySource).map(([source, count]) => (
                  <div key={source} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 capitalize">{source.replace('_', ' ')}</span>
                    <span className="font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </>
      )}

      {activeTab === 'vulnerabilities' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search vulnerabilities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="risk_accepted">Risk Accepted</option>
            </select>

            <select
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Sources</option>
              <option value="npm_audit">npm Audit</option>
              <option value="dependabot">Dependabot</option>
              <option value="semgrep">Semgrep</option>
              <option value="manual">Manual</option>
            </select>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vulnerability
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SLA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVulnerabilities.map(vuln => (
                  <tr key={vuln.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{vuln.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {vuln.cve_id && (
                            <span className="text-xs text-blue-600">{vuln.cve_id}</span>
                          )}
                          {vuln.package_name && (
                            <span className="text-xs text-gray-500">{vuln.package_name}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${getSeverityColor(vuln.severity)}`}>
                        {vuln.severity.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${getStatusColor(vuln.status)}`}>
                        {vuln.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {vuln.slaStatus && (
                        <div className={`flex items-center gap-1 ${getSLAStatusColor(vuln.slaStatus.status)}`}>
                          {vuln.slaStatus.status === 'breached' ? (
                            <XCircle size={14} />
                          ) : vuln.slaStatus.status === 'completed' ? (
                            <CheckCircle size={14} />
                          ) : (
                            <Clock size={14} />
                          )}
                          <span className="text-sm">
                            {vuln.slaStatus.status === 'completed' ? 'Done' :
                             vuln.slaStatus.status === 'breached' ? `${Math.abs(vuln.slaStatus.daysRemaining)}d overdue` :
                             `${vuln.slaStatus.daysRemaining}d left`}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {vuln.assignee ? (
                        <span className="text-sm text-gray-600">{vuln.assignee.name}</span>
                      ) : (
                        <span className="text-sm text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleVulnerabilityClick(vuln.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                      >
                        View <ChevronRight size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredVulnerabilities.length === 0 && (
              <div className="text-center py-12">
                <Shield size={48} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900">No vulnerabilities found</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Run a scan or adjust your filters
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'reports' && quarterlyReport && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Q{quarterlyReport.period.quarter} {quarterlyReport.period.year} Report
            </h3>
            <button
              onClick={handleDownloadReport}
              disabled={isDownloading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download size={16} />
              )}
              {isDownloading ? 'Downloading...' : 'Download PDF'}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <span className="text-sm text-gray-500">Discovered</span>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{quarterlyReport.summary.discovered}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <span className="text-sm text-gray-500">Resolved</span>
              <p className="text-2xl font-semibold text-green-600 mt-1">{quarterlyReport.summary.resolved}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <span className="text-sm text-gray-500">SLA Breaches</span>
              <p className={`text-2xl font-semibold mt-1 ${quarterlyReport.summary.slaBreaches > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {quarterlyReport.summary.slaBreaches}
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <span className="text-sm text-gray-500">Resolution Rate</span>
              <p className="text-2xl font-semibold text-blue-600 mt-1">{quarterlyReport.summary.resolutionRate}%</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Vulnerabilities by Severity</h4>
              <div className="space-y-3">
                {Object.entries(quarterlyReport.bySeverity).map(([severity, count]) => (
                  <div key={severity} className="flex items-center justify-between">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${getSeverityColor(severity)}`}>
                      {severity.toUpperCase()}
                    </span>
                    <span className="font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Compliance Summary</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Mean Time to Remediate</span>
                  <span className="font-semibold text-gray-900">{quarterlyReport.summary.mttrDays} days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">SLA Compliance Rate</span>
                  <span className="font-semibold text-gray-900">{quarterlyReport.summary.slaComplianceRate}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Open at Period End</span>
                  <span className="font-semibold text-gray-900">{quarterlyReport.summary.openAtEnd}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Risk Accepted</span>
                  <span className="font-semibold text-gray-900">{quarterlyReport.summary.riskAccepted}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Vulnerability Settings</h3>
            {hasSettingsChanges && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleResetSettings}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSettings}
                  disabled={isSavingSettings}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSavingSettings ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  Save Changes
                </button>
              </div>
            )}
          </div>

          {!editedSettings ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-blue-600" />
              <span className="ml-2 text-gray-500">Loading settings...</span>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">SLA Configuration</h4>
                <p className="text-sm text-gray-500 mb-4">Set the number of days to remediate vulnerabilities by severity level</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { key: 'sla_zero_day', label: 'Zero-Day', color: 'purple' },
                    { key: 'sla_critical', label: 'Critical', color: 'red' },
                    { key: 'sla_high', label: 'High', color: 'orange' },
                    { key: 'sla_medium', label: 'Medium', color: 'amber' },
                    { key: 'sla_low', label: 'Low', color: 'green' },
                    { key: 'sla_info', label: 'Info', color: 'blue' }
                  ].map(({ key, label, color }) => (
                    <div key={key} className="p-3 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className={`inline-block w-2 h-2 rounded-full bg-${color}-500 mr-2`}></span>
                        {label}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={editedSettings[key as keyof VulnerabilitySettings] as number}
                          onChange={(e) => handleSettingsChange(key as keyof VulnerabilitySettings, parseInt(e.target.value) || 1)}
                          className="w-20 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span className="text-sm text-gray-500">days</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-medium text-gray-900 mb-2">Notification Settings</h4>
                <p className="text-sm text-gray-500 mb-4">Configure when to send alerts and who receives them</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email alert recipients
                    </label>
                    <select
                      value={recipientRolesValue}
                      onChange={(e) =>
                        handleSettingsChange(
                          'notify_recipient_roles',
                          e.target.value === 'super_and_admin'
                            ? ['SUPER_ADMIN', 'ADMIN']
                            : ['SUPER_ADMIN']
                        )
                      }
                      className="w-full max-w-md px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="super_only">Super Admins only</option>
                      <option value="super_and_admin">Super Admins and Admins</option>
                    </select>
                    <p className="mt-1.5 text-xs text-gray-500">
                      Default is Super Admins only so vulnerability emails are not sent to every Admin account.
                      Assignees still receive alerts for items assigned to them.
                    </p>
                  </div>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editedSettings.notify_new_vulnerability}
                      onChange={(e) => handleSettingsChange('notify_new_vulnerability', e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Email notifications for new vulnerabilities</span>
                  </label>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={editedSettings.notify_sla_warning}
                      onChange={(e) => handleSettingsChange('notify_sla_warning', e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">SLA warning at</span>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={editedSettings.notify_sla_warning_threshold}
                      onChange={(e) => handleSettingsChange('notify_sla_warning_threshold', parseInt(e.target.value) || 75)}
                      disabled={!editedSettings.notify_sla_warning}
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                    />
                    <span className="text-sm text-gray-700">% time used</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={editedSettings.notify_escalation}
                      onChange={(e) => handleSettingsChange('notify_escalation', e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Escalation at</span>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={editedSettings.notify_escalation_threshold}
                      onChange={(e) => handleSettingsChange('notify_escalation_threshold', parseInt(e.target.value) || 90)}
                      disabled={!editedSettings.notify_escalation}
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                    />
                    <span className="text-sm text-gray-700">% time used</span>
                  </div>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editedSettings.notify_sla_breach}
                      onChange={(e) => handleSettingsChange('notify_sla_breach', e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">SLA breach notifications</span>
                  </label>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-medium text-gray-900 mb-2">Compliance Standards</h4>
                <p className="text-sm text-gray-500 mb-4">This dashboard helps maintain compliance with:</p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    SOC 2 CC7.1
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    SOC 2 CC7.2
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    SOC 2 CC7.4
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ISO 27001 A.12.6.1
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ISO 27001 A.16.1.5
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ISO 27001 A.18.2.3
                  </span>
                </div>
              </div>

              {settings?.updated_at && (
                <div className="border-t border-gray-200 pt-4 mt-6">
                  <p className="text-xs text-gray-400">
                    Last updated: {new Date(settings.updated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VulnerabilityDashboard;
